/*
 *
 * Dynamic Score Reports helpers
 *
 */
import React from "react";
import {
  DEVELOP_LANDING_PAGE_BASE_LINK,
  SI_LANDING_PAGE_BASE_LINK,
  dynamicTraitFieldToPretty,
} from "./constants";

// determines if a test is invalid - different from utils/helperFunctions isInvalidTest since it takes
// a test's report object. logic copied from api ResultsController checkForInvalid
export const showInvalidTestWarning = (testId, reportData) => {
  if (!reportData) {
    return false;
  }
  switch (true) {
    case testId === 10 && reportData.get("details").get("fraudDetected") === 1:
    case testId === 31 &&
      reportData.get("details").get("FAKING_percentile") > 95:
    case testId === 34 && reportData.get("details").get("INC_score") >= 3:
    case [15, 24].includes(testId) &&
      reportData.get("details").get("INC_score") >= 5:
    case testId === 13 && reportData.get("text").get("validity_text"):
    case testId === 46 && reportData.get("details").get("invalid") === 1:
    case testId === 48 &&
      reportData.get("details").get("inconsistencyPercentile") >= 98 &&
      reportData.get("percentile") !== null:
      return true;
    default:
      return false;
  }
};

export const getScoreRangeName = (scoreRangeData, testId, eppJobMatchName) => {
  if (!scoreRangeData) {
    return "";
  }
  if (testId === "34" && eppJobMatchName) {
    // EPP job family name
    return dynamicTraitFieldToPretty[testId][eppJobMatchName];
  }
  if (scoreRangeData.get("selectedPosition")) {
    // Default score range name
    return scoreRangeData.get("selectedPosition");
  }
  if (
    scoreRangeData.get("scoreRanges") &&
    scoreRangeData.get("scoreRanges").size !== 0
  ) {
    // Custom score range name
    return scoreRangeData.get("scoreRanges").first().get("testBaselineName");
  }

  return "";
};

// Only for rendering anchor elements used for PDF downloads
export const getDownloadAnchorElements = (candidateInfo) => {
  const pdfNames = ["summary", "testReport", "workplaceInsights", "workStyles"];

  return pdfNames.map((pdfName) => (
    <a
      key={pdfName}
      id={`${pdfName}DwnldLnk`}
      download={`${candidateInfo.get("testTakerFirstName")}-${candidateInfo.get(
        "testTakerLastName"
      )}`}
      style={{ display: "none" }}
    >
      dwnld
    </a>
  ));
};

export const getIllustraitNoCompetencySetText = (fromSummary) =>
  fromSummary ? (
    <div className="pr-1">
      <p>
        If you have Criteria access, a competency set must be added in Job
        Settings in order to see a score report. Once a competency set has been
        added in Job Settings, score reports will only be available for future
        candidates and will not change previously completed candidate results.
      </p>
      <p className="mb-0">
        If you do not have Criteria access, please reach out to your Criteria
        administrator.
      </p>
    </div>
  ) : (
    <p className="mb-0">
      There are no results available for Illustrait because of the settings for
      this assessment. See the Work Styles report for more information on this
      candidate.
    </p>
  );

export const waaWithoutWorkFactorsText = (
  <div className="pr-1">
    <p>
      If you have Criteria access, add Work Factors in Job Settings to view a
      match.
    </p>
    <p className="mb-0">
      If you do not have Criteria access, please reach out to your Criteria
      administrator.
    </p>
  </div>
);

// Takes an all uppercase string and only capitalizes first letter of each word, all other letters to lowercase
export const capitalizeFirstLetterOfWords = (string) => {
  const words = string.split(" ");

  return words
    .map((word) => word[0] + word.substring(1).toLowerCase())
    .join(" ");
};

export const getSubTestInfoDescription = (subTestId) => {
  switch (subTestId) {
    case "191":
    case "204":
    case "205":
    case "206":
    case "207":
    case "208":
    case "209":
    case "210":
    case "211":
    case "212":
    case "213":
    case "214":
    case "215":
      return `The CLPT Express - English measures proficiency in the English language across the skills of reading, writing, and listening. The test measures proficiency from beginner to advanced levels.`;
    case "240":
      return `The CTRS Express measures cognitive aptitude, or general intelligence, across a variety of domains. This test provides an indication of a candidate’s ability to process and use different types of information to solve problems. Cognitive aptitude is one of the most accurate predictors of job success for any position.`;
    default:
      return null;
  }
};

export const getCompanyStatus = (hasDevelop, hasSI) => {
  if (hasDevelop && hasSI) {
    return "dsrCompanyHasSiAndDevelop";
  }
  if (hasDevelop && !hasSI) {
    return "dsrCompanyHasDevelopOnly";
  }
  if (!hasDevelop && hasSI) {
    return "dsrCompanyHasSiOnly";
  }
  if (hasDevelop === false && hasSI === false) {
    return "dsrCompanyHasNone";
  }
};

export const getDSRMerchLandingPageURL = (str, firstName, lastName, medium) => {
  const isSIorDevelop = str.split("-")[1];
  if (isSIorDevelop === "si") {
    return `${SI_LANDING_PAGE_BASE_LINK}calendly=criteria-${firstName}-${lastName}&utm_content=${str}&utm_campaign=${isSIorDevelop}&utm_source=dsr-landing&utm_medium=${medium}`;
  }
  if (isSIorDevelop === "develop") {
    return `${DEVELOP_LANDING_PAGE_BASE_LINK}calendly=criteria-${firstName}-${lastName}&utm_content=${str}&utm_campaign=${isSIorDevelop}&utm_source=dsr-landing&utm_medium=${medium}`;
  }
};

export const scheduleCalendlyCallWithAmOrCsm = (str) => {
  const [, isSIorDevelop, isUpgradeOrAdopt] = str.split("-");

  if (
    (isSIorDevelop === "si" && isUpgradeOrAdopt === "upgrade") ||
    (isSIorDevelop === "develop" && isUpgradeOrAdopt === "upgrade")
  ) {
    return "accountManager";
  }

  return "customerSuccessManager";
};
